import { useEffect, useState } from "react";
import { can, AGE_GROUPS_DISPLAY } from "../data/constants";
import { useReviewDetail } from "../hooks/useReviewDetail";

// ── SVG assets for the view product drawer ──────────────────────────────────
import CrossIcon from "../assets/product/viewproduct/cross.svg";
import ProductsIcon from "../assets/product/viewproduct/Products.svg";
import IngredientsIcon from "../assets/product/viewproduct/ingredients.svg";
import NutritionIcon from "../assets/product/viewproduct/nutrition.svg";
import EditIcon from "../assets/product/viewproduct/edit.svg";
import ApproveIcon from "../assets/product/viewproduct/approve.svg";
import RejectIcon from "../assets/product/viewproduct/reject.svg";

export default function ProductViewDrawer({
  open,
  product,
  onClose,
  onApprove,
  onReject,
  onEdit,
  userRole,
}) {
  const [visible, setVisible] = useState(false);
  const [animIn, setAnimIn] = useState(false);

  // Fetch the full review details using the review-details API
  const { detail, loading: loadingDetail } = useReviewDetail(product?.product_id || product?.id || null);

  // Handle mount / unmount animation
  useEffect(() => {
    if (open) {
      setVisible(true);
      requestAnimationFrame(() => requestAnimationFrame(() => setAnimIn(true)));
    } else {
      setAnimIn(false);
      const t = setTimeout(() => setVisible(false), 300);
      return () => clearTimeout(t);
    }
  }, [open]);

  if (!visible || !product) return null;

  // ── Derive display data from API submission/OCR or fallback to product ──
  const sub = detail?.submission || {};
  const reviewId = detail?.id || product.id || "—";
  const brand = sub.brand_name || product.brand || "—";
  const gtin = sub.gtin || detail?.gtin || product.gtin || "—";
  const submittedBy = sub.agent_id || product.createdBy || "—";
  const rawDate = sub.submitted_at || detail?.created_at || product.createdDate;
  const formattedDate = rawDate
    ? new Date(rawDate).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "—";

  // OCR/API ingredients list or fallback
  const ocrIngredients = detail?.ocr?.ingredients
    ? [...detail.ocr.ingredients].sort((a, b) => a.position - b.position)
    : [];
  const ingredients = ocrIngredients.length > 0
    ? ocrIngredients.map((i) => i.name)
    : (product.ingredients
        ? (typeof product.ingredients === "string"
            ? product.ingredients.split(",").map((s) => s.trim()).filter(Boolean)
            : product.ingredients)
        : []);

  // OCR/API nutrients list or fallback
  const ocrNutrition = detail?.ocr?.nutrition_facts || [];
  const uniqueOcrNutrition = [];
  const seenNutrients = new Set();
  for (const n of ocrNutrition) {
    if (n && n.nutrient_key && !seenNutrients.has(n.nutrient_key)) {
      seenNutrients.add(n.nutrient_key);
      uniqueOcrNutrition.push(n);
    }
  }

  const LABELS = {
    energy:        "Energy",
    carbohydrate:  "Carbohydrate",
    sugar:         "of which Sugars",
    fat:           "Fat",
    saturated_fat: "of which Saturates",
    protein:       "Protein",
    sodium:        "Sodium",
    fibre:         "Dietary Fibre",
    salt:          "Salt",
  };

  const nutrients = uniqueOcrNutrition.length > 0
    ? uniqueOcrNutrition.map((row) => ({
        label: LABELS[row.nutrient_key] || row.nutrient_key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
        value: row.value,
        unit: row.unit || "",
        dv: row.dv || "",
      }))
    : (product.nutrients
        ? (typeof product.nutrients === "object" && !Array.isArray(product.nutrients)
            ? Object.entries(product.nutrients).map(([label, val]) => ({
                label,
                value: val?.value ?? val,
                unit: val?.unit ?? "",
                dv: val?.dv ?? "",
              }))
            : Array.isArray(product.nutrients)
            ? product.nutrients
            : [])
        : []);

  const images = product.imageUrl ? [product.imageUrl] : [];

  const ageGroupKey = product.ageGroup || detail?.age_group || sub.age_group || null;
  const ageGroupVal = AGE_GROUPS_DISPLAY[ageGroupKey] || ageGroupKey || "—";

  const detailRows = [
    { label: "Review Id", value: reviewId },
    { label: "Brand", value: brand, isBlue: true },
    { label: "GTIN", value: gtin },
    { label: "Age Group", value: ageGroupVal },
    { label: "Submitted By", value: submittedBy },
    { label: "Date", value: formattedDate },
  ];

  return (
    <>
      {/* ── Overlay backdrop ────────────────────────────────────────────── */}
      <div
        onClick={onClose}
        className="fixed inset-0 z-40 transition-opacity duration-300"
        style={{
          background: "#0B61721A",
          backdropFilter: "blur(14px)",
          WebkitBackdropFilter: "blur(14px)",
          opacity: animIn ? 1 : 0,
        }}
      />

      {/* ── Drawer panel ────────────────────────────────────────────────── */}
      <div
        className="fixed top-0 right-0 h-full z-50 flex flex-col transition-transform duration-300 ease-in-out"
        style={{
          width: 400,
          transform: animIn ? "translateX(0)" : "translateX(100%)",
        }}
      >
        {/* Inner scrollable card */}
        <div
          className="flex flex-col h-full overflow-hidden"
          style={{
            background: "#FFFFFF",
            border: "1px solid #0000001A",
            borderRadius: "20px 0 0 20px",
          }}
        >
          {/* ── Header: name + GTIN + close ─────────────────────────────── */}
          <div className="flex items-start justify-between px-5 pt-5 pb-4 shrink-0">
            <div>
              <h2 className="text-[18px] font-bold text-gray-900 leading-tight m-0">
                {product.name || "Product"}
              </h2>
              <p className="text-[12px] text-gray-400 mt-0.5">
                GTIN: {gtin || "N/A"}
              </p>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors border-none bg-transparent cursor-pointer shrink-0"
            >
              <img src={CrossIcon} alt="close" className="w-4 h-4" />
            </button>
          </div>

          {/* ── Scrollable body ──────────────────────────────────────────── */}
          <div className="flex-1 overflow-y-auto px-5 pb-4 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">

            {/* Product image + thumbnails */}
            <div className="flex gap-2 mb-5">
              {/* Main image */}
              <div className="flex-1 rounded-[14px] bg-gray-50 border border-gray-100 overflow-hidden min-h-[180px] flex items-center justify-center">
                {product.imageUrl ? (
                  <img
                    src={product.imageUrl}
                    alt={product.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span className="text-5xl">📦</span>
                )}
              </div>
              {/* Thumbnail strip */}
              {images.length > 0 && (
                <div className="flex flex-col gap-2 w-[70px] shrink-0">
                  {images.slice(0, 3).map((url, i) => (
                    <div
                      key={i}
                      className={`w-full h-[60px] rounded-lg border-2 overflow-hidden ${
                        i === 0 ? "border-[#0B6172]" : "border-gray-200"
                      }`}
                    >
                      <img src={url} alt={`thumb-${i}`} className="w-full h-full object-cover" />
                    </div>
                  ))}
                </div>
              )}
            </div>

            {loadingDetail ? (
              <div className="flex flex-col items-center justify-center py-10">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0B6172]" />
                <span className="text-sm text-gray-500 mt-2">Loading review details...</span>
              </div>
            ) : (
              <>
                {/* ── Product Details section ───────────────────────────────── */}
                <div className="mb-4">
                  {/* Section header */}
                  <div className="flex items-center gap-[14px] mb-[18px]">
                    <div className="w-10 h-10 rounded-[10px] bg-[#39B8AE] flex items-center justify-center text-white">
                      <img src={ProductsIcon} alt="details" className="w-5 h-5" />
                    </div>
                    <span className="text-[18px] font-bold text-[#132238]">Product Details</span>
                  </div>

                  {/* Detail rows */}
                  <div className="flex flex-col gap-2">
                    {detailRows.map((row) => (
                      <div
                        key={row.label}
                        className="flex justify-between items-center bg-[#E6F0F1] rounded-[10px] px-4 py-3.5"
                      >
                        <span className="text-[16px] text-[#2C3A4B] font-medium">{row.label}</span>
                        <span className={`text-[16px] font-bold ${row.isBlue ? "text-[#0E7184]" : "text-[#132238]"} truncate max-w-[55%]`} title={row.value}>
                          {row.value}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* ── Ingredients section ───────────────────────────────────── */}
                <div className="mb-4">
                  {/* Section header */}
                  <div className="flex items-center gap-[14px] mb-[18px]">
                    <div className="w-10 h-10 rounded-[10px] bg-[#39B8AE] flex items-center justify-center text-white">
                      <img src={IngredientsIcon} alt="ingredients" className="w-5 h-5" />
                    </div>
                    <span className="text-[18px] font-bold text-[#132238]">Ingredients</span>
                  </div>

                  {ingredients.length === 0 ? (
                    <p className="text-[13px] text-gray-400 py-2">No ingredient data available.</p>
                  ) : (
                    <div className="flex flex-col gap-2">
                      {ingredients.map((ing, i) => {
                        const name = typeof ing === "string" ? ing : ing.name || ing;
                        return (
                          <div
                            key={i}
                            className="flex justify-between items-center bg-[#E6F0F1] rounded-[10px] px-4 py-3.5"
                          >
                            <span className="text-[16px] text-[#2C3A4B] font-medium">{name}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* ── Nutrition Facts section ───────────────────────────────── */}
                <div className="mb-2">
                  {/* Section header */}
                  <div className="flex items-center gap-[14px] mb-[18px]">
                    <div className="w-10 h-10 rounded-[10px] bg-[#39B8AE] flex items-center justify-center text-white">
                      <img src={NutritionIcon} alt="nutrition" className="w-5 h-5" />
                    </div>
                    <span className="text-[18px] font-bold text-[#132238]">Nutrition Facts</span>
                  </div>

                  {nutrients.length === 0 ? (
                    <p className="text-[13px] text-gray-400 py-2">No nutrition data available.</p>
                  ) : (
                    <div className="flex flex-col gap-2">
                      {nutrients.map((n, i) => (
                        <div
                          key={i}
                          className="flex justify-between items-center bg-[#E6F0F1] rounded-[10px] px-4 py-3.5"
                        >
                          <span className="text-[16px] text-[#2C3A4B] font-medium">{n.label}</span>
                          <span className="text-[16px] font-bold text-[#132238]">
                            {n.value}{n.unit} {n.dv ? `(${n.dv}%)` : ""}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>

          {/* ── Footer: action buttons ───────────────────────────────────── */}
          <div className="shrink-0 px-5 py-4 border-t border-[#0000001A] flex items-center gap-3">
            {/* Edit */}
            {can(userRole, "edit_p") && (
              <button
                onClick={() => { onClose(); onEdit(); }}
                className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-[13px] font-semibold text-gray-600 cursor-pointer transition-colors hover:opacity-80"
                style={{
                  border: "1px solid #5E5E5E33",
                  background: "#5E5E5E1A",
                }}
              >
                <img src={EditIcon} alt="edit" className="w-4 h-4" />
                Edit
              </button>
            )}

            {/* Approve */}
            {can(userRole, "approve") && product.status !== "approved" && (
              <button
                onClick={() => { onClose(); onApprove(); }}
                className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-[13px] font-semibold text-[#0B6172] cursor-pointer transition-colors hover:opacity-80"
                style={{
                  border: "1px solid #0B617233",
                  background: "#0B61721A",
                }}
              >
                <img src={ApproveIcon} alt="approve" className="w-4 h-4" />
                Approve
              </button>
            )}

            {/* Reject */}
            {can(userRole, "reject") && product.status !== "rejected" && (
              <button
                onClick={() => { onClose(); onReject(); }}
                className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-[13px] font-semibold text-red-600 cursor-pointer transition-colors hover:opacity-80"
                style={{
                  background: "#FF00001A",
                  border: "1px solid #FF000033",
                }}
              >
                <img src={RejectIcon} alt="reject" className="w-4 h-4" />
                Reject
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
